import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Course } from '@/types';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import NavBar from '@/components/NavBar';
import CourseSearchInline from '@/components/courses/CourseSearchInline';
import AddCourseDialog from '@/components/courses/AddCourseDialog';
import { useCourses } from '@/hooks/useCourses';
import { setHomeCourse, addCourseToUser } from '@/integrations/supabase/profileCourseService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { MapPin, Home, Star, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';

const Courses = () => {
  const { user } = useAuth();
  const { userCourses, loading, handleToggleFavorite, refreshUserCourses } = useCourses();
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [homeCourse, setHomeCourse_] = useState<{ id: string; name: string; location?: string } | null>(null);

  useEffect(() => {
    const fetchHomeCourse = async () => {
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('home_course_id')
        .eq('id', user.id)
        .single();

      if (profile?.home_course_id) {
        const { data: course } = await supabase
          .from('courses')
          .select('id, name, city, state')
          .eq('id', profile.home_course_id)
          .single();

        if (course) {
          setHomeCourse_({
            id: course.id,
            name: course.name,
            location: [course.city, course.state].filter(Boolean).join(', '),
          });
        }
      }
    };

    fetchHomeCourse();
  }, [user]);

  const handleSelectCourse = (course: Course) => {
    setSelectedCourse(course);
  };

  const handleSetHomeCourse = async () => {
    if (!user || !selectedCourse) return;

    setIsSaving(true);
    try {
      await addCourseToUser(user.id, selectedCourse.id, true);

      const result = await setHomeCourse(user.id, selectedCourse.id);
      if (result.error) {
        throw result.error;
      }

      setHomeCourse_({
        id: selectedCourse.id,
        name: selectedCourse.name,
        location: selectedCourse.location,
      });
      setSelectedCourse(null);

      toast({
        title: 'Home course updated!',
        description: `${selectedCourse.name} is now your home course.`,
      });
    } catch (error) {
      console.error('Error setting home course:', error);
      toast({
        title: 'Error',
        description: 'Failed to set your home course. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <Header title="Courses" />

      {/* Home course banner */}
      <div className="mx-4 mb-4 p-3 bg-forest/5 border border-forest/20 rounded-lg flex items-center gap-3">
        <Home className="h-5 w-5 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          {homeCourse ? (
            <>
              <p className="text-xs text-muted-foreground">Home Course</p>
              <p className="font-medium truncate">{homeCourse.name}</p>
              {homeCourse.location && (
                <p className="text-xs text-muted-foreground truncate">{homeCourse.location}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No home course set — select one below</p>
          )}
        </div>
      </div>

      {/* Selected course confirmation banner */}
      {selectedCourse && selectedCourse.id !== homeCourse?.id && (
        <div className="mx-4 mb-4 p-3 bg-sky/10 border border-sky/30 rounded-lg flex items-center gap-3">
          <MapPin className="h-5 w-5 text-sky shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{selectedCourse.name}</p>
            {selectedCourse.location && (
              <p className="text-xs text-muted-foreground truncate">{selectedCourse.location}</p>
            )}
          </div>
          <Button
            size="sm"
            className="bg-forest hover:bg-forest/90 text-white shrink-0"
            onClick={handleSetHomeCourse}
            disabled={isSaving}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set as Home'}
          </Button>
        </div>
      )}

      {/* Inline API search */}
      <div className="px-4 mb-6">
        <CourseSearchInline onImported={refreshUserCourses} />
      </div>

      {/* My Courses */}
      <div className="px-4">
        <h2 className="font-display text-lg font-semibold text-forest mb-3">My Courses</h2>
        {loading ? (
          <div className="space-y-1">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex-1">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28 mt-1" />
                </div>
                <Skeleton className="h-4 w-4" />
              </div>
            ))}
          </div>
        ) : userCourses.length === 0 ? (
          <EmptyState
            title="No courses yet"
            description="Search above to import one."
          />
        ) : (
          <div className="space-y-1">
            {userCourses.map(course => (
              <div
                key={course.id}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectCourse(course)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectCourse(course); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{course.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{course.location}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(course);
                  }}
                  className="p-1 shrink-0"
                >
                  <Star className={`h-4 w-4 ${course.is_favorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'}`} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manual add fallback */}
      <div className="px-4 mt-6 text-center">
        <AddCourseDialog
          onAddCourse={refreshUserCourses}
          trigger={
            <button className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors">
              Can't find your course? Add it manually
            </button>
          }
        />
      </div>

      <NavBar />
    </div>
  );
};

export default Courses;
